import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileTextIcon,
  LinkIcon,
  Loader2Icon,
  PlusIcon,
  RefreshCwIcon,
  TrashIcon,
  XCircleIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'

// ── Types ────────────────────────────────────────────────────────────

interface CityRecord {
  id: string
  property_id: string
  source: string
  source_uid: string
  record_date: string | null
  status: string | null
  category: string | null
  title: string | null
  description: string | null
  amount_due: number | null
  // summons doc fields
  summons_doc_path: string | null
  violation_narrative: string | null
  ocr_status: string | null
  // workflow fields
  linked_job_id: string | null
  responsibility: 'ours' | 'not_ours' | 'unsure' | null
  responsibility_note: string | null
  dismissed: boolean
  first_seen_at: string
  last_synced_at: string
}

interface Props {
  propertyId: string
  customerId: string | null
}

// ── Source metadata ───────────────────────────────────────────────────

const SOURCE_META: Record<
  string,
  { label: string; color: string; isEnforcement: boolean }
> = {
  dsny_oath:     { label: 'DSNY Tickets',       color: 'orange', isEnforcement: true  },
  oath_ecb:      { label: 'OATH/ECB Tickets',   color: 'orange', isEnforcement: true  },
  dob_ecb:       { label: 'DOB/ECB Violations', color: 'red',    isEnforcement: true  },
  hpd_violation: { label: 'HPD Violations',     color: 'red',    isEnforcement: false },
  hpd_complaint: { label: 'HPD Complaints',     color: 'yellow', isEnforcement: false },
  complaint_311: { label: '311 Complaints',     color: 'blue',   isEnforcement: false },
  pluto:         { label: 'Property Facts',     color: 'gray',   isEnforcement: false },
  acris:         { label: 'ACRIS Documents',    color: 'gray',   isEnforcement: false },
}

const COLOR_CLASSES: Record<string, { badge: string; border: string }> = {
  orange: { badge: 'bg-orange-100 text-orange-800', border: 'border-orange-200' },
  red:    { badge: 'bg-red-100 text-red-800',       border: 'border-red-200'    },
  yellow: { badge: 'bg-yellow-100 text-yellow-800', border: 'border-yellow-200' },
  blue:   { badge: 'bg-blue-100 text-blue-800',     border: 'border-blue-200'   },
  gray:   { badge: 'bg-gray-100 text-gray-700',     border: 'border-gray-200'   },
  green:  { badge: 'bg-green-100 text-green-800',   border: 'border-green-200'  },
}

const RESPONSIBILITY_LABELS = {
  ours:     { label: 'Our Responsibility', color: 'red'   },
  not_ours: { label: 'Not Our Responsibility', color: 'green' },
  unsure:   { label: 'Under Review', color: 'yellow' },
}

// ── Component ─────────────────────────────────────────────────────────

export default function CityRecordsTab({ propertyId, customerId }: Props) {
  const navigate = useNavigate()
  const [records, setRecords]           = useState<CityRecord[]>([])
  const [loading, setLoading]           = useState(true)
  const [refreshing, setRefreshing]     = useState(false)
  const [collapsed, setCollapsed]       = useState<Record<string, boolean>>({})
  const [expanded, setExpanded]         = useState<Record<string, boolean>>({})
  const [editing, setEditing]           = useState<string | null>(null)
  const [respNote, setRespNote]         = useState('')
  const [downloading, setDownloading]   = useState<string | null>(null)
  const [lastSync, setLastSync]         = useState<string | null>(null)

  // ── Fetch records from Supabase ───────────────────────────────────

  const fetchRecords = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('property_city_records')
      .select('*')
      .eq('property_id', propertyId)
      .eq('dismissed', false)
      .order('record_date', { ascending: false })

    if (!error && data) {
      setRecords(data as CityRecord[])
      if (data.length > 0) {
        const latest = data.reduce((a, b) =>
          a.last_synced_at > b.last_synced_at ? a : b
        )
        setLastSync(latest.last_synced_at)
      }
    }
    setLoading(false)
  }, [propertyId])

  useEffect(() => { fetchRecords() }, [fetchRecords])

  // ── Trigger a live refresh via Express ───────────────────────────

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const resp = await fetch(`/api/property/${propertyId}/refresh-city-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || 'Refresh failed')
      await fetchRecords()
    } catch (err: any) {
      alert(`Refresh failed: ${err.message}`)
    } finally {
      setRefreshing(false)
    }
  }

  // ── Dismiss record ────────────────────────────────────────────────

  const handleDismiss = async (recordId: string) => {
    await supabase
      .from('property_city_records')
      .update({ dismissed: true })
      .eq('id', recordId)
    setRecords((prev) => prev.filter((r) => r.id !== recordId))
  }

  // ── Set responsibility ────────────────────────────────────────────

  const handleResponsibility = async (
    recordId: string,
    responsibility: 'ours' | 'not_ours' | 'unsure' | null,
    note?: string
  ) => {
    await supabase
      .from('property_city_records')
      .update({ responsibility, responsibility_note: note || null, reviewed_at: new Date().toISOString() })
      .eq('id', recordId)
    setRecords((prev) =>
      prev.map((r) => r.id === recordId ? { ...r, responsibility, responsibility_note: note || null } : r)
    )
    setEditing(null)
    setRespNote('')
  }

  // ── Create work order from record ─────────────────────────────────

  const handleCreateJob = (rec: CityRecord) => {
    const params = new URLSearchParams({
      property_id:          propertyId,
      ...(customerId ? { customer_id: customerId } : {}),
      source_city_record_id: rec.id,
      title:  rec.title || rec.source_uid,
      description: [rec.description, rec.violation_narrative]
        .filter(Boolean).join('\n\n'),
    })
    navigate(`/jobs/create?${params.toString()}`)
  }

  // ── Download summons document ─────────────────────────────────────

  const handleDownload = async (rec: CityRecord) => {
    setDownloading(rec.id)
    try {
      const resp = await fetch(`/api/property/city-record/${rec.id}/summons`)
      if (!resp.ok) throw new Error('Document not available')
      const blob = await resp.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `${rec.source_uid}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      alert(`Download failed: ${err.message}`)
    } finally {
      setDownloading(null)
    }
  }

  // ── Derived data ──────────────────────────────────────────────────

  const bySource: Record<string, CityRecord[]> = {}
  for (const rec of records) {
    if (!bySource[rec.source]) bySource[rec.source] = []
    bySource[rec.source].push(rec)
  }

  const dsnyPendingReview = (bySource['dsny_oath'] || []).filter(
    (r) => !r.responsibility && (r.amount_due || 0) > 0
  )
  const dsnyAmountAtRisk = dsnyPendingReview.reduce(
    (sum, r) => sum + (r.amount_due || 0), 0
  )
  const dsnyOurs = (bySource['dsny_oath'] || []).filter(
    (r) => r.responsibility === 'ours'
  )
  const dsnyOursTotal = dsnyOurs.reduce(
    (sum, r) => sum + (r.amount_due || 0), 0
  )

  // Source display order
  const sourceOrder = [
    'dsny_oath', 'dob_ecb', 'hpd_violation', 'hpd_complaint', 'complaint_311', 'pluto', 'acris'
  ]

  // ── Render helpers ────────────────────────────────────────────────

  const statusBadge = (status: string | null) => {
    if (!status) return null
    const s = status.toLowerCase()
    const isOpen = s.includes('open') || s.includes('new') || s.includes('pending') || s.includes('due')
    const isClosed = s.includes('close') || s.includes('resolve') || s.includes('paid') || s.includes('cure')
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
        isOpen   ? 'bg-red-100 text-red-700'   :
        isClosed ? 'bg-green-100 text-green-700' :
                   'bg-gray-100 text-gray-600'
      }`}>
        {status}
      </span>
    )
  }

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

  const fmtMoney = (n: number | null) =>
    n != null && n > 0 ? `$${n.toFixed(2)}` : null

  // ── Render ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2Icon className="w-6 h-6 animate-spin text-blue-600 mr-3" />
        <span className="text-gray-600">Loading city records…</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">NYC City Records</h3>
          {lastSync && (
            <p className="text-xs text-gray-500 mt-0.5">
              Last synced {fmtDate(lastSync)}
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <RefreshCwIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Fetching…' : 'Refresh from City'}
        </button>
      </div>

      {/* DSNY responsibility summary banner */}
      {dsnyPendingReview.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangleIcon className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-orange-900">
              {dsnyPendingReview.length} DSNY ticket{dsnyPendingReview.length !== 1 ? 's' : ''} pending responsibility review
            </p>
            <p className="text-sm text-orange-700">
              ${dsnyAmountAtRisk.toFixed(2)} potentially at risk
              {dsnyOurs.length > 0 && ` · $${dsnyOursTotal.toFixed(2)} already marked as ours`}
            </p>
          </div>
        </div>
      )}

      {records.length === 0 && (
        <div className="text-center py-16 text-gray-500">
          <FileTextIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No city records yet.</p>
          <p className="text-sm mt-1">Click "Refresh from City" to pull data from NYC Open Data.</p>
        </div>
      )}

      {/* Source sections */}
      {sourceOrder.map((source) => {
        const recs = bySource[source]
        if (!recs || recs.length === 0) return null
        const meta = SOURCE_META[source] || { label: source, color: 'gray', isEnforcement: false }
        const colors = COLOR_CLASSES[meta.color]
        const isCollapsed = collapsed[source]
        const totalDue = recs.reduce((s, r) => s + (r.amount_due || 0), 0)

        return (
          <div key={source} className={`border rounded-lg overflow-hidden ${colors.border}`}>
            {/* Section header */}
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
              onClick={() => setCollapsed((p) => ({ ...p, [source]: !p[source] }))}
            >
              <div className="flex items-center gap-3">
                {isCollapsed
                  ? <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                  : <ChevronDownIcon  className="w-4 h-4 text-gray-400" />}
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                  {meta.label}
                </span>
                <span className="text-sm text-gray-600">{recs.length} record{recs.length !== 1 ? 's' : ''}</span>
                {totalDue > 0 && (
                  <span className="text-sm font-semibold text-red-700">${totalDue.toFixed(2)} due</span>
                )}
              </div>
            </button>

            {/* Records list */}
            {!isCollapsed && (
              <div className="divide-y divide-gray-100">
                {recs.map((rec) => {
                  const isExpanded = expanded[rec.id]
                  const isEditingThis = editing === rec.id
                  const resp = rec.responsibility
                    ? RESPONSIBILITY_LABELS[rec.responsibility]
                    : null

                  return (
                    <div key={rec.id} className="bg-white">
                      {/* Record row */}
                      <div className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {rec.title || rec.source_uid}
                              </span>
                              {statusBadge(rec.status)}
                              {rec.amount_due != null && rec.amount_due > 0 && (
                                <span className="text-sm font-bold text-red-700">
                                  {fmtMoney(rec.amount_due)}
                                </span>
                              )}
                              {resp && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${COLOR_CLASSES[resp.color].badge}`}>
                                  {resp.label}
                                </span>
                              )}
                              {rec.linked_job_id && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                                  Linked to Job
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span>{fmtDate(rec.record_date)}</span>
                              {rec.category && <span>· {rec.category}</span>}
                              {rec.source_uid && <span className="font-mono">#{rec.source_uid}</span>}
                            </div>
                          </div>

                          {/* Row actions */}
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => setExpanded((p) => ({ ...p, [rec.id]: !p[rec.id] }))}
                              className="p-1.5 text-gray-400 hover:text-gray-700 rounded"
                              title={isExpanded ? 'Collapse' : 'Expand details'}
                            >
                              {isExpanded
                                ? <ChevronDownIcon  className="w-4 h-4" />
                                : <ChevronRightIcon className="w-4 h-4" />}
                            </button>

                            {/* Download — enforcement only */}
                            {meta.isEnforcement && (
                              <button
                                onClick={() => handleDownload(rec)}
                                disabled={downloading === rec.id}
                                className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                                title="Download summons document"
                              >
                                {downloading === rec.id
                                  ? <Loader2Icon className="w-4 h-4 animate-spin" />
                                  : <DownloadIcon className="w-4 h-4" />}
                              </button>
                            )}

                            {/* Create work order */}
                            <button
                              onClick={() => handleCreateJob(rec)}
                              className="p-1.5 text-gray-400 hover:text-green-600 rounded"
                              title="Create work order from this record"
                            >
                              <PlusIcon className="w-4 h-4" />
                            </button>

                            {/* DSNY responsibility — dsny_oath only */}
                            {source === 'dsny_oath' && (
                              <button
                                onClick={() => { setEditing(isEditingThis ? null : rec.id); setRespNote(rec.responsibility_note || '') }}
                                className="p-1.5 text-gray-400 hover:text-orange-600 rounded"
                                title="Set responsibility"
                              >
                                <AlertTriangleIcon className="w-4 h-4" />
                              </button>
                            )}

                            {/* Dismiss */}
                            <button
                              onClick={() => handleDismiss(rec.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                              title="Dismiss"
                            >
                              <XCircleIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Responsibility editor */}
                        {isEditingThis && (
                          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                            <p className="text-xs font-semibold text-orange-900">
                              Is this ticket our responsibility?
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              {(['ours', 'not_ours', 'unsure'] as const).map((v) => (
                                <button
                                  key={v}
                                  onClick={() => handleResponsibility(rec.id, v, respNote)}
                                  className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-colors ${
                                    rec.responsibility === v
                                      ? 'bg-orange-600 text-white border-orange-600'
                                      : 'bg-white text-gray-700 border-gray-200 hover:border-orange-400'
                                  }`}
                                >
                                  {RESPONSIBILITY_LABELS[v].label}
                                </button>
                              ))}
                              {rec.responsibility && (
                                <button
                                  onClick={() => handleResponsibility(rec.id, null)}
                                  className="px-3 py-1.5 text-xs rounded-lg font-medium border border-gray-200 bg-white text-gray-500 hover:border-gray-400"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            <textarea
                              value={respNote}
                              onChange={(e) => setRespNote(e.target.value)}
                              placeholder="Optional note (e.g. 'Covered by job #J-0042')"
                              rows={2}
                              className="w-full text-xs border border-gray-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
                            />
                            <button
                              onClick={() => { setEditing(null); setRespNote('') }}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100 space-y-3">
                          {rec.description && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</p>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">{rec.description}</p>
                            </div>
                          )}

                          {rec.violation_narrative && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Inspector's Violation Details
                                {rec.ocr_status === 'done' && (
                                  <span className="ml-2 normal-case font-normal text-gray-400">
                                    (extracted from summons)
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap bg-amber-50 border border-amber-200 rounded p-2">
                                {rec.violation_narrative}
                              </p>
                            </div>
                          )}

                          {rec.responsibility_note && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Responsibility Note</p>
                              <p className="text-sm text-gray-700">{rec.responsibility_note}</p>
                            </div>
                          )}

                          {/* Action buttons in expanded view */}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {meta.isEnforcement && (
                              <button
                                onClick={() => handleDownload(rec)}
                                disabled={downloading === rec.id}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                <DownloadIcon className="w-3.5 h-3.5" />
                                Download Summons
                                {rec.ocr_status === 'pending' && (
                                  <span className="ml-1 text-blue-200">(OCR pending)</span>
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleCreateJob(rec)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700"
                            >
                              <PlusIcon className="w-3.5 h-3.5" />
                              Create Work Order
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
